/**
 * Pontos de contato BLACKFEO (faixa, destaque, CTA de artigo e pop-up): gerais
 * levam ao catálogo; nas páginas de produto, ao checkout do próprio produto. Antes da virada só aparecem com a prévia (?preview=1).
 */
import { useEffect, useRef, useState } from "react";
import { ArrowRight, X } from "lucide-react";
import logoBlue from "@/assets/blackfeo/logo-1.webp.asset.json";
import { BLACKFEO_CATALOGO, comPreviewAntesDaVirada } from "@/lib/black-november/ofertas-lps";
import { proximoResetBlackfeo } from "@/lib/black-november/blackfeo-contador";
import { BLACKFEO_TAGLINE, BLACKFEO_TAGLINE_CLS } from "@/lib/black-november/tagline";

export const BLACKFEO_CHAMADA = "Todos os nossos produtos com até 80% de desconto";

/** Link ao catálogo com ponto de contato + UTMs/trk da visita (aplicado no clique). */
export function blackfeoHref(ponto: string): string {
  const base = comPreviewAntesDaVirada(`${BLACKFEO_CATALOGO}?ponto=${encodeURIComponent(ponto)}`, Date.now());
  if (typeof window === "undefined") return base;
  const u = new URL(base, window.location.origin);
  new URLSearchParams(window.location.search).forEach((v, k) => {
    if ((k.startsWith("utm_") || k === "trk" || k === "tracker_trk") && !u.searchParams.has(k)) u.searchParams.set(k, v);
  });
  return u.pathname + u.search;
}
function linkCatalogo(ponto: string) {
  const set = (e: React.MouseEvent<HTMLAnchorElement>) => { e.currentTarget.href = blackfeoHref(ponto); };
  return { href: `${BLACKFEO_CATALOGO}?ponto=${encodeURIComponent(ponto)}`, onMouseDown: set, onClick: set };
}

const FAIXA_CLS = "mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-4 gap-y-1.5 px-4 py-2.5 text-center text-xs font-bold sm:text-sm";
function FaixaConteudo({ off }: { off: string }) {
  return (
    <>
      <span className="whitespace-nowrap text-sm font-black uppercase tracking-[0.18em] text-black-campaign-lilac sm:text-base">BLACK FEO</span>
      <span>{off}</span>
      <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[0.7rem] uppercase text-primary-foreground">Garantir oferta <ArrowRight className="h-3 w-3" aria-hidden /></span>
    </>
  );
}

/**
 * Faixa no topo. Sem `produto`: páginas gerais → catálogo ("ATÉ 80% OFF").
 * Com `produto`: página de venda → desconto do próprio produto e botão que
 * abre o checkout (onClick) ou leva às ofertas da página (href), nunca ao catálogo.
 */
function ContadorProduto({ off }: { off: string }) {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const rest = now == null ? 0 : Math.max(0, Math.floor((proximoResetBlackfeo(now) - now) / 1000));
  const items: Array<[string, number]> = [
    ["Dias", Math.floor(rest / 86400)],
    ["Horas", Math.floor((rest % 86400) / 3600)],
    ["Minutos", Math.floor((rest % 3600) / 60)],
    ["Segundos", rest % 60],
  ];
  return (
    <div className="mx-auto flex max-w-3xl flex-col items-center gap-2.5 px-4 py-4 sm:py-5">
      <p className="text-center text-xs font-extrabold uppercase tracking-[0.18em] text-black-campaign-lilac sm:text-sm">BLACK FEO · {off}</p>
      <div className="flex items-start justify-center gap-2.5" aria-live="off">
        {items.map(([label, v], i) => (
          <div key={label} className="flex items-start gap-2.5">
            <div className="text-center">
              <div className="text-lg font-extrabold leading-none tabular-nums text-foreground sm:text-xl">{String(v).padStart(2, "0")}</div>
              <div className="mt-1 text-[10px] font-semibold text-muted-foreground">{label}</div>
            </div>
            {i < items.length - 1 && <span className="text-lg font-extrabold leading-none text-black-campaign-lilac/60">:</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function FaixaFixaProduto({ off, href, onClick, alvo }: { off: string; href?: string; onClick?: () => void; alvo: React.RefObject<HTMLDivElement | null> }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const upd = () => { const el = alvo.current; setOn(!!el && el.getBoundingClientRect().bottom < 0); };
    window.addEventListener("scroll", upd, { passive: true }); upd();
    return () => window.removeEventListener("scroll", upd);
  }, [alvo]);
  const cls = "block w-full py-1.5 text-center text-[11px] font-extrabold uppercase tracking-[0.18em] text-black-campaign-lilac sm:text-xs";
  const txt = <>BLACK FEO · {off}</>;
  return (
    <div aria-hidden={!on} className={`blackfeo-scope fixed inset-x-0 top-0 z-50 border-b border-black-campaign-lilac/40 transition-transform duration-300 ${on ? "translate-y-0" : "-translate-y-full"}`}>
      {onClick ? <button type="button" tabIndex={on ? 0 : -1} onClick={onClick} className={cls}>{txt}</button> : <a href={href ?? "#oferta"} tabIndex={on ? 0 : -1} className={cls}>{txt}</a>}
    </div>
  );
}

export function BlackfeoTopBar({ ponto, produto, fixa = true }: { ponto: string; produto?: { off: string; href?: string; onClick?: () => void }; fixa?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  if (produto) {
    return (
      <div ref={ref} className="blackfeo-scope border-b border-black-campaign-lilac/40">
        {fixa && <FaixaFixaProduto off={produto.off} href={produto.href} onClick={produto.onClick} alvo={ref} />}
        {produto.onClick ? (
          <button type="button" onClick={produto.onClick} className="block w-full"><ContadorProduto off={produto.off} /></button>
        ) : (
          <a href={produto.href ?? "#oferta"} className="block"><ContadorProduto off={produto.off} /></a>
        )}
      </div>
    );
  }
  return (
    <div className="blackfeo-scope border-b border-border">
      <a {...linkCatalogo(ponto)} className={FAIXA_CLS}><FaixaConteudo off="ATÉ 80% OFF" /></a>
    </div>
  );
}

/** Bloco de destaque (home, institucionais e artigos). */
export function BlackfeoDestaque({ ponto, compacto = false }: { ponto: string; compacto?: boolean }) {
  return (
    <section aria-label="BLACKFEO" className={`blackfeo-scope relative isolate overflow-hidden text-center ${compacto ? "rounded-2xl px-6 py-8 sm:py-10" : "px-5 py-12 sm:py-16"}`}>
      <div aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-primary/30 blur-[120px]" />
      <div aria-hidden className="pointer-events-none absolute -left-24 bottom-0 h-64 w-64 rounded-full bg-accent/25 blur-[120px]" />
      <div className="relative mx-auto max-w-3xl">
        <img src={logoBlue.url} alt="BLACKFEO" width={863} height={507} loading="lazy" decoding="async" className={`mx-auto h-auto ${compacto ? "w-44 sm:w-56" : "w-52 sm:w-64"}`} />
        <p className={`mt-5 font-black leading-tight ${compacto ? "text-xl sm:text-2xl" : "text-2xl sm:text-4xl"}`}>
          Todos os nossos produtos com<br /><span className="text-black-campaign-lilac">até 80% de desconto</span>
        </p>
        <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground sm:text-base">Pós-graduações, certificações, STAR Cursos e Agente IA com os menores preços do ano. Escolha o seu e garanta a sua vaga.</p>
        <p className={`mt-4 ${BLACKFEO_TAGLINE_CLS}`}>{BLACKFEO_TAGLINE}</p>
        <a {...linkCatalogo(ponto)} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-black uppercase tracking-wide text-primary-foreground no-underline hover:brightness-110">
          Ver ofertas <ArrowRight className="h-4 w-4" aria-hidden />
        </a>
      </div>
    </section>
  );
}

/**
 * Pop-up BLACKFEO (uma vez por sessão). `rolagem` só nos artigos sorteados do
 * E-FISIO (50% da leitura ou saída); nas demais páginas, só intenção de saída.
 * Sem `destino` leva ao catálogo; com `destino` (página de produto) leva ao checkout.
 */
export function BlackfeoPopup({ ponto, modo = "saida", destino }: { ponto: string; modo?: "rolagem" | "saida"; destino?: { off: string; href?: string; onClick?: () => void } }) {
  const [aberto, setAberto] = useState(false);
  useEffect(() => {
    const KEY = "feo-blackfeo-popup";
    try { if (sessionStorage.getItem(KEY)) return; } catch { /* sem storage */ }
    let feito = false;
    let lastY = window.scrollY, maxY = lastY;
    const mostrar = () => {
      if (feito) return; feito = true;
      try { sessionStorage.setItem(KEY, "1"); } catch { /* sem storage */ }
      setAberto(true); limpar();
    };
    const onScroll = () => {
      const h = document.documentElement;
      const y = window.scrollY;
      maxY = Math.max(maxY, y);
      if (modo === "rolagem" && (h.scrollTop + window.innerHeight) / h.scrollHeight > 0.5) mostrar();
      // Celular não tem "saída" pelo mouse: subida rápida depois de ler a página.
      else if (window.matchMedia("(pointer:coarse)").matches && maxY > 900 && lastY - y > 120) mostrar();
      lastY = y;
    };
    const onSaida = (e: MouseEvent) => { if (!e.relatedTarget && e.clientY <= 0) mostrar(); };
    const limpar = () => { window.removeEventListener("scroll", onScroll); document.removeEventListener("mouseout", onSaida); };
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseout", onSaida);
    return limpar;
  }, [modo]);
  useEffect(() => {
    if (!aberto) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setAberto(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aberto]);
  if (!aberto) return null;
  const cta = "mt-6 inline-flex items-center gap-2 rounded-xl bg-primary px-7 py-3 text-sm font-black uppercase tracking-wide text-primary-foreground no-underline hover:brightness-110";
  return (
    <div role="dialog" aria-modal="true" aria-label="BLACKFEO" className="blackfeo-scope fixed inset-0 z-[100] flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm" onClick={(e) => e.target === e.currentTarget && setAberto(false)}>
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-border bg-background px-6 py-9 text-center shadow-2xl">
        <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-primary/30 blur-[100px]" />
        <div aria-hidden className="pointer-events-none absolute -left-20 -bottom-20 h-48 w-48 rounded-full bg-accent/25 blur-[100px]" />
        <button type="button" aria-label="Fechar" onClick={() => setAberto(false)} className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><X className="h-5 w-5" /></button>
        <div className="relative">
          <img src={logoBlue.url} alt="BLACKFEO" width={863} height={507} decoding="async" className="mx-auto h-auto w-52" />
          {destino ? (
            <p className="mt-5 text-3xl font-black uppercase leading-tight text-black-campaign-lilac">{destino.off.replace(/\s*OFF$/i, "")} de desconto</p>
          ) : (
            <p className="mt-5 text-2xl font-black leading-tight">Todos os nossos produtos<br />com <span className="text-black-campaign-lilac">até 80% de desconto</span></p>
          )}
          {destino ? (
            <p className={`mt-4 ${BLACKFEO_TAGLINE_CLS}`}>Oferta exclusiva por tempo limitado</p>
          ) : (
            <>
              <p className="mt-3 text-sm text-muted-foreground">Pós-graduações, certificações, STAR Cursos e Agente IA com os menores preços do ano.</p>
              <p className={`mt-4 ${BLACKFEO_TAGLINE_CLS}`}>{BLACKFEO_TAGLINE}</p>
            </>
          )}
          {destino ? (
            destino.onClick ? (
              <button type="button" onClick={() => { setAberto(false); destino.onClick?.(); }} className={cta}>Garantir oferta <ArrowRight className="h-4 w-4" aria-hidden /></button>
            ) : (
              <a href={destino.href ?? "#oferta"} onClick={() => setAberto(false)} className={cta}>Garantir oferta <ArrowRight className="h-4 w-4" aria-hidden /></a>
            )
          ) : (
            <a {...linkCatalogo(`${ponto}-popup-${modo}`)} className={cta}>Ver ofertas <ArrowRight className="h-4 w-4" aria-hidden /></a>
          )}
        </div>
      </div>
    </div>
  );
}

/** Artigos do E-FISIO com pop-up de rolagem: 30% fixos por slug (mesmo artigo, mesma regra). */
export function artigoComPopupRolagem(slug: string): boolean {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return h % 100 < 30;
}
